/**
 * Screen Wake Lock — keep the field's iPhone awake for the duration of
 * an inspection session.
 *
 * Why: long inspections (15+ min of camera + talking) blow past iOS's
 * default screen timeout. When the screen sleeps, getUserMedia stops
 * pushing frames; the operator sees a frozen feed; the field user
 * sometimes has to re-grant camera permission on wake. The Wake Lock
 * API holds the screen on while the page is active.
 *
 * Browser support: iOS Safari 16.4+ (Apr 2023), Android Chrome since
 * forever. Gracefully no-ops on older browsers — the lock is a UX
 * polish, not a correctness requirement.
 *
 * Auto-release rules:
 *   - On `visibilitychange` to `hidden` (user backgrounds the tab —
 *     iOS auto-releases anyway, but we mirror it explicitly + re-
 *     acquire on `visible` so the lock survives short backgrounding).
 *   - On explicit `release()`.
 *   - On page unload (browser handles this).
 *
 * Usage:
 *   const lock = await acquireWakeLock();
 *   // ... session work ...
 *   await lock.release();
 *
 * Or via `useWakeLock()` for React components.
 */
import { useEffect } from "react";


type ManagedWakeLock = {
  release: () => Promise<void>;
};


/** Acquire a screen wake lock. Resolves to a `release()` callable
 * regardless of whether the lock was actually granted — callers don't
 * need to special-case unsupported browsers. */
export async function acquireWakeLock(): Promise<ManagedWakeLock> {
  if (typeof navigator === "undefined" || !("wakeLock" in navigator)) {
    return { release: async () => {} };
  }

  let sentinel: WakeLockSentinel | null = null;
  let released = false;

  const acquire = async () => {
    if (released || document.visibilityState !== "visible") return;
    try {
      sentinel = await navigator.wakeLock.request("screen");
      sentinel.addEventListener("release", () => {
        // The OS can revoke the lock at any time (low battery, etc.).
        // Clear our reference so a subsequent visibilitychange triggers
        // a fresh request.
        sentinel = null;
      });
    } catch {
      // Permission denied / OS refused. Not fatal — session continues
      // without the screen-on guarantee.
    }
  };

  const onVisibility = () => {
    if (document.visibilityState === "visible") {
      void acquire();
    }
  };

  document.addEventListener("visibilitychange", onVisibility);
  await acquire();

  return {
    release: async () => {
      released = true;
      document.removeEventListener("visibilitychange", onVisibility);
      if (sentinel) {
        try {
          await sentinel.release();
        } catch {
          /* already released — no-op */
        }
        sentinel = null;
      }
    },
  };
}


/** React hook — acquires a wake lock on mount, releases on unmount.
 * Use it inside FieldRoom / OperatorRoom so the lock is held only while
 * a session is open. */
export function useWakeLock(active: boolean = true): void {
  useEffect(() => {
    if (!active) return;
    let lock: ManagedWakeLock | null = null;
    let cancelled = false;
    void acquireWakeLock().then((l) => {
      if (cancelled) {
        void l.release();
      } else {
        lock = l;
      }
    });
    return () => {
      cancelled = true;
      if (lock) {
        void lock.release();
      }
    };
  }, [active]);
}
